import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { aiAPI } from '../services/aiAPI';
import toast from 'react-hot-toast';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -60%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const Overlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Modal = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-xl);
  width: 90vw;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideIn} 0.3s ease-out;
`;

const Header = styled.div`
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid var(--color-gray-200);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--color-gray-500);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--border-radius-md);
  font-size: 1.5rem;
  line-height: 1;
  
  &:hover {
    background: var(--color-gray-100);
    color: var(--color-gray-700);
  }
`;

const Content = styled.div`
  padding: 1.5rem 2rem;
`;

const SectionTitle = styled.h3`
  font-family: var(--font-sans);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-gray-700);
  margin: 0 0 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const OriginalText = styled.div`
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--color-gray-700);
`;

const ImprovementTypes = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const TypeButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'var(--color-primary)' : 'var(--color-gray-100)'};
  color: ${props => props.active ? 'var(--color-gray-800)' : 'var(--color-gray-600)'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--color-primary-dark)' : 'var(--color-gray-200)'};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--color-gray-600);
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-gray-300);
  border-top: 2px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuggestionContainer = styled.textarea`
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: 0.375rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #1F2937;
  white-space: pre-wrap;
  font-family: inherit;
  resize: vertical;
  min-height: 150px;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #10B981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
  }
`;

const Actions = styled.div`
  padding: 1rem 2rem 1.5rem;
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? '#059669' : '#6B7280'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;

  &:hover {
    background: ${props => props.variant === 'primary' ? '#047857' : '#4B5563'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

interface AISuggestionModalProps {
  selectedText: string;
  visible: boolean;
  onClose: () => void;
  onAccept: (suggestion: string) => void;
}

const AISuggestionModal: React.FC<AISuggestionModalProps> = ({
  selectedText,
  visible,
  onClose,
  onAccept
}) => {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [editedSuggestion, setEditedSuggestion] = useState('');
  const [improvementType, setImprovementType] = useState('general');
  const [ageRange, setAgeRange] = useState('6-8');

  const improvementTypes = [
    { value: 'general', label: 'General' },
    { value: 'dialogue', label: 'Dialogue' },
    { value: 'description', label: 'Description' },
    { value: 'pacing', label: 'Pacing' },
    { value: 'clarity', label: 'Clarity' },
    { value: 'engagement', label: 'Engagement' }
  ];

  const ageRanges = [
    { value: '3-5', label: '3-5 years' },
    { value: '6-8', label: '6-8 years' },
    { value: '9-12', label: '9-12 years' },
    { value: '13-16', label: '13-16 years' },
    { value: '16-18', label: '16-18 years' },
    { value: '18+', label: '18+ years' }
  ];

  // Remove automatic generation - only generate when Generate button is clicked
  useEffect(() => {
    if (visible) {
      // Reset state when modal opens
      setSuggestion('');
      setEditedSuggestion('');
    }
  }, [visible]);

  const generateSuggestion = async () => {
    if (!selectedText.trim()) return;

    setLoading(true);
    setSuggestion('');

    try {
      const response = await aiAPI.improveStory(selectedText, improvementType, ageRange);
      console.log('AI response:', response);
      if (response.success) {
        console.log('AI suggestion received:', response.content.substring(0, 100) + '...');
        setSuggestion(response.content);
        setEditedSuggestion(response.content);
      } else {
        console.error('AI request failed:', response.message);
        toast.error('Failed to generate suggestion: ' + response.message);
        onClose();
      }
    } catch (error: any) {
      console.error('AI request error:', error);
      toast.error('Failed to generate AI suggestion: ' + error.message);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (editedSuggestion.trim()) {
      onAccept(editedSuggestion);
      toast.success('AI suggestion applied!');
      onClose();
    }
  };

  const handleReject = () => {
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <Overlay visible={visible} onClick={handleOverlayClick}>
      <Modal>
        <Header>
          <Title>
            🤖 AI Writing Assistant
          </Title>
          <CloseButton onClick={onClose}>
            ×
          </CloseButton>
        </Header>

        <Content>
          <SectionTitle>Original Text</SectionTitle>
          <OriginalText>
            "{selectedText}"
          </OriginalText>

          <SectionTitle>Improvement Type</SectionTitle>
          <ImprovementTypes>
            {improvementTypes.map((type) => (
              <TypeButton
                key={type.value}
                active={improvementType === type.value}
                onClick={() => setImprovementType(type.value)}
                disabled={loading}
              >
                {type.label}
              </TypeButton>
            ))}
          </ImprovementTypes>

          <SectionTitle>Age Range</SectionTitle>
          <ImprovementTypes>
            {ageRanges.map((range) => (
              <TypeButton
                key={range.value}
                active={ageRange === range.value}
                onClick={() => setAgeRange(range.value)}
                disabled={loading}
              >
                {range.label}
              </TypeButton>
            ))}
          </ImprovementTypes>

          {!suggestion && !loading && (
            <Actions>
              <ActionButton 
                variant="primary" 
                onClick={generateSuggestion}
                disabled={!selectedText.trim()}
              >
                🤖 Generate Suggestion
              </ActionButton>
            </Actions>
          )}

          {loading && (
            <LoadingContainer>
              <LoadingSpinner />
              Generating {improvementType} improvement...
            </LoadingContainer>
          )}

          {suggestion && !loading && (
            <>
              <SectionTitle>AI Suggestion (Editable)</SectionTitle>
              <SuggestionContainer
                value={editedSuggestion}
                onChange={(e) => setEditedSuggestion(e.target.value)}
                placeholder="AI suggestion will appear here..."
              />
            </>
          )}
        </Content>

        {suggestion && !loading && (
          <Actions>
            <ActionButton variant="secondary" onClick={handleReject}>
              ✕ Reject
            </ActionButton>
            <ActionButton 
              variant="primary" 
              onClick={handleAccept}
              disabled={!editedSuggestion || !editedSuggestion.trim()}
            >
              ✓ Accept
            </ActionButton>
          </Actions>
        )}
      </Modal>
    </Overlay>
  );
};

export default AISuggestionModal;